import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import ru from './locales/ru.json';

/**
 * Локализация.
 *
 * Каждый язык — один JSON-файл, внутри которого пространства имён верхнего
 * уровня (common, auth, errors…). Чтобы добавить язык, достаточно положить
 * файл рядом и дописать одну строку в LOCALE_FILES — код менять не нужно (F-30).
 */

const LOCALE_FILES: Record<string, Record<string, unknown>> = {
  ru,
  en,
};

export interface LocaleMeta {
  code: string;
  name: string;
  nativeName: string;
  intlLocale: string;
  flag: string;
}

export const AVAILABLE_LOCALES: LocaleMeta[] = Object.values(LOCALE_FILES).map(
  (file) => file.meta as LocaleMeta,
);

export const NAMESPACES = Array.from(
  new Set(Object.values(LOCALE_FILES).flatMap((file) => Object.keys(file))),
).filter((ns) => ns !== 'meta');

const STORAGE_KEY = 'eb-locale';

export function getStoredLocale(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && LOCALE_FILES[stored]) return stored;
  } catch {
    /* приватный режим */
  }
  const browser = navigator.language.slice(0, 2);
  return LOCALE_FILES[browser] ? browser : 'ru';
}

export function storeLocale(locale: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* приватный режим */
  }
}

const resources = Object.fromEntries(
  Object.entries(LOCALE_FILES).map(([code, file]) => [code, file as Record<string, object>]),
);

void i18next.use(initReactI18next).init({
  resources,
  lng: getStoredLocale(),
  fallbackLng: 'ru',
  ns: NAMESPACES,
  defaultNS: 'common',
  interpolation: { escapeValue: false },
  returnNull: false,
  // В разработке хотим видеть отсутствующие ключи, а не молчаливый фолбэк.
  saveMissing: false,
  missingKeyHandler:
    import.meta.env.DEV
      ? (lngs, ns, key) => {
          console.warn(`[i18n] нет перевода: ${ns}:${key} (${lngs.join(', ')})`);
        }
      : undefined,
});

export function changeLocale(locale: string): void {
  if (!LOCALE_FILES[locale]) return;
  void i18next.changeLanguage(locale);
  storeLocale(locale);
  document.documentElement.lang = locale;
}

export function currentIntlLocale(): string {
  const meta = LOCALE_FILES[i18next.language]?.meta as LocaleMeta | undefined;
  return meta?.intlLocale ?? 'ru-RU';
}

document.documentElement.lang = i18next.language;

export default i18next;
